import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';

const CODES = [
  { code: 'CODE_1', label: 'Code 1 — Approved', hint: 'Advances to the next level.' },
  {
    code: 'CODE_2',
    label: 'Code 2 — Approved as noted',
    hint: 'Advances, but your comments must be closed out in the next revision.',
  },
  {
    code: 'CODE_3',
    label: 'Code 3 — Revise and resubmit',
    hint: 'Returns to the originator. A new revision will be required.',
  },
  { code: 'CODE_4', label: 'Code 4 — Rejected', hint: 'Returns to the originator.' },
] as const;

/**
 * Radios, not a dropdown. All four outcomes must be readable at once
 * — this is an expensive choice to get wrong, and a collapsed select
 * hides the three options the reviewer did not think of.
 */
export function ActionDialog({
  stepId,
  docNumber,
  revCode,
  onClose,
  onDone,
}: {
  stepId: string;
  docNumber: string;
  revCode: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [code, setCode] = useState<string>('CODE_1');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Comment is mandatory for everything except a clean approval —
  // enforced here for feedback and again on the server for truth.
  const needsComment = code !== 'CODE_1';
  const blocked = busy || (needsComment && comment.trim() === '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key !== 'Tab' || !ref.current) return;
      // Trap focus: a modal the keyboard can wander out of is a modal
      // for mouse users only.
      const items = ref.current.querySelectorAll<HTMLElement>(
        'button, input, textarea, [href], [tabindex]:not([tabindex="-1"])',
      );
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/steps/${stepId}/actions`, { code, comment });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record the review.');
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Review document" ref={ref}>
        <h2>Review</h2>
        <p className="sub">
          {docNumber} · Rev {revCode}
        </p>

        {error && (
          <div className="banner err" role="alert">
            {error}
          </div>
        )}

        <div className="codes" role="radiogroup" aria-label="Review code">
          {CODES.map((c) => (
            <label key={c.code} className={`code-opt ${code === c.code ? 'sel' : ''}`}>
              <input
                type="radio"
                name="code"
                value={c.code}
                checked={code === c.code}
                onChange={() => setCode(c.code)}
              />
              <span>
                <b>{c.label}</b>
                <span>{c.hint}</span>
              </span>
            </label>
          ))}
        </div>

        <label className="field">
          <span>Comment {needsComment ? '(required)' : '(optional)'}</span>
          <textarea
            className="textarea"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
              needsComment
                ? 'What needs to change, and why? This is what the originator will act on.'
                : 'Anything worth recording.'
            }
          />
          {needsComment && comment.trim() === '' && (
            <span className="hint">
              Every code except a clean approval must say why — a reason nobody wrote is a reason
              nobody can act on.
            </span>
          )}
        </label>

        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={blocked}>
            {busy ? 'Recording…' : 'Submit review'}
          </button>
        </div>
      </div>
    </div>
  );
}
